import React from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { whyboxHeading } from '../panelHeading';

export type WhyThisResultLevel = 'card' | 'metric';

/**
 * One rendered `Why this result?` reason. Structurally what `CardReason` already is, restated here
 * so this component keeps its existing independence from the Intelligence container's types.
 */
export interface IWhyThisResultReason {
  code: string;
  text: string;
}

export interface IWhyThisResultBoxProps {
  level: WhyThisResultLevel;
  /** The composed explanation sentence; the FR fixes its wording per level, not this component. */
  text: string;
  /**
   * The backend's own reasons, already rendered to sentences and already in the order it ranked
   * them. Drawn as a list ABOVE `text`, which stays the pipeline's `calculation_basis` paragraph.
   * Nothing is composed, re-ordered or formatted here. PLA-8828.
   */
  reasons?: IWhyThisResultReason[];
  /** Metric-level only — qualifies the heading when one box sits beside several others. */
  metricName?: string;
  /**
   * Overrides the level's own heading. The screens stack a second box of exactly this treatment
   * headed `Evidence`, so the shape is shared and only the words differ.
   */
  heading?: string;
  className?: string;
}

const Box = styled.section`
  padding: ${tokens.space['12']};
  border: 1px solid ${tokens.color['border-strong']};
  border-radius: ${tokens.radius.sm};

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). The doubled class
     outranks the children's own margin reset, which is injected later and would otherwise win the
     cascade on equal specificity and collapse this gap to nothing. */
  && > * + * {
    margin-top: ${tokens.space['4']};
  }
`;

// The container title step (Body2 13), at the box heading's own weight. PLA-8844.
const Heading = styled.h4`
  ${whyboxHeading}
  font-size: ${tokens.type['card-title-size']};
  line-height: ${tokens.type['card-title-line']};
`;

const Explanation = styled.p`
  margin: 0;
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

/* An ordered list, because the order IS the backend's ranking and a reader who re-sorted the page
   would otherwise lose it. Typography is the paragraph's, so a card carrying reasons and a card
   carrying a basis read as the same box. No icons, tones or badges: there is no design for this
   treatment yet, and inventing one would state a severity the wire does not. */
const Reasons = styled.ol`
  margin: 0;
  padding-left: ${tokens.space['16']};
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

const Reason = styled.li`
  && + & {
    margin-top: ${tokens.space['4']};
  }
`;

const WhyThisResultBox: React.FC<IWhyThisResultBoxProps> = ({
  level,
  text,
  reasons,
  metricName,
  heading,
  className
}) => {
  const { t } = useMultiLanguageTranslation();

  const explanation = typeof text === 'string' ? text.trim() : '';
  const listed = (Array.isArray(reasons) ? reasons : []).filter(
    reason => reason && typeof reason.text === 'string' && reason.text.trim() !== ''
  );

  // The FR forbids fabricating an explanation, so an empty one leaves no titled shell behind either.
  // A card with reasons and no basis still has something to say, which is why both are checked.
  if (explanation.length === 0 && listed.length === 0) {
    return null;
  }

  const resolvedHeading =
    heading ??
    (level === 'metric' && metricName
      ? t(`${TCustomerInsights}.WhyThisResult.MetricHeading`, { metricName })
      : t(`${TCustomerInsights}.WhyThisResult.Heading`));

  return (
    <Box className={className} data-testid="why-this-result-box" data-level={level}>
      <Heading data-testid="why-this-result-box-heading">{resolvedHeading}</Heading>
      {listed.length > 0 ? (
        <Reasons data-testid="why-this-result-box-reasons">
          {listed.map((reason, order) => (
            <Reason
              key={`${reason.code}-${order}`}
              data-reason-code={reason.code}
              data-testid="why-this-result-box-reason"
            >
              {reason.text}
            </Reason>
          ))}
        </Reasons>
      ) : null}
      {explanation.length > 0 ? (
        <Explanation data-testid="why-this-result-box-text">{explanation}</Explanation>
      ) : null}
    </Box>
  );
};

export default WhyThisResultBox;
